/**
 * The typed HTTP client.
 *
 * Every network call the application makes goes through this file. Keeping
 * them together means the sync engine, the uploader and the UI all agree on
 * what the server returns, and there is exactly one place to look when a
 * request shape changes.
 *
 * DATA DEFINITION
 *   An ApiError is an Error carrying the HTTP status.
 *   Interpretation: the server refused, and the caller may want to distinguish
 *   "not signed in" (401) from "not allowed" (403) from everything else.
 */
import type { Change, Comment, Lock, Presence, User } from '@lattice/core'

export interface ProjectDto {
  readonly id: string
  readonly name: string
  readonly description: string
  readonly sortOrder: number
}

export interface Bootstrap {
  readonly user: User
  readonly roster: readonly User[]
  readonly projects: readonly ProjectDto[]
  readonly changes: readonly Change[]
  readonly comments: readonly Comment[]
  readonly locks: readonly Lock[]
  readonly cursor: number
  readonly epoch: string
  readonly presence: readonly Presence[]
  readonly readMark: number
}

export interface Delta {
  readonly changes: readonly Change[]
  readonly comments: readonly Comment[]
  readonly locks?: readonly Lock[]
  readonly cursor: number
  readonly epoch?: string
}

/** The first frame of an event stream; see the server's /api/stream. */
export interface Hello {
  readonly epoch: string
  readonly cursor: number
  /** True when this client's replica refers to a log that no longer exists. */
  readonly resync: boolean
  readonly you: string
  /** Identifies this stream, so we can tell the server where we are looking. */
  readonly connection: string
}

export type UploadTicket =
  | { kind: 'exists' }
  | { kind: 'upload'; url: string; method: 'PUT' | 'POST'; headers: Record<string, string> }

export interface DownloadItem {
  readonly path: string
  readonly name: string
  readonly sha256: string
  readonly size: number
  readonly url: string | null
  readonly contentType?: string
  readonly reason?: string
}

export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Issue a JSON request, turning a non-2xx into an ApiError. */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: { 'content-type': 'application/json', ...(init?.headers ?? {}) },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }))
    throw new ApiError((body as { error?: string }).error ?? 'request failed', response.status)
  }
  return response.json() as Promise<T>
}

const post = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: JSON.stringify(body) })


/* ------------------------------------------------------------------ */
/* Mutation results                                                    */
/* ------------------------------------------------------------------ */

/**
 * Every mutating endpoint returns the rows the server actually wrote, with the
 * sequence numbers it assigned. Publishing them here means the caller sees its
 * own change immediately, by the shortest path, without any call site having
 * to remember to do it — and without this module importing the store, which
 * would make a cycle.
 *
 * DATA DEFINITION
 *   A MutationResult is { changes, comments }.
 *   Interpretation: authoritative rows, not an optimistic guess. The event
 *   stream will deliver the same rows a moment later; merging is by id, so the
 *   second arrival changes nothing.
 */
export interface MutationResult {
  readonly changes?: readonly Change[]
  readonly comments?: readonly Comment[]
  readonly locks?: readonly Lock[]
}

const mutationListeners = new Set<(result: MutationResult) => void>()

/** Be told what every mutating call wrote. onMutation : fn -> (-> void) */
export function onMutation(listener: (result: MutationResult) => void): () => void {
  mutationListeners.add(listener)
  return () => { mutationListeners.delete(listener) }
}

/** Announce a write. A throwing listener must not fail the request. */
function published<T extends MutationResult>(result: T): T {
  for (const listener of mutationListeners) {
    try { listener(result) } catch { /* a view's problem, not the network's */ }
  }
  return result
}

export const api = {
  health: () => request<{ ok: boolean; auth: string; storage: string }>('/api/health'),

  authConfig: () => request<{ google: boolean; dev: boolean; roster: { id: string; name: string; email: string }[] }>('/auth/config'),

  devSignIn: (email: string) => post<{ user: User }>('/auth/dev', { email }),

  signOut: () => post<{ ok: true }>('/auth/logout', {}),

  bootstrap: () => request<Bootstrap>('/api/bootstrap'),

  /**
   * Just who we are and what projects exist — used when the local replica is
   * already warm and we only need the tail of the log, not all of it.
   */
  bootstrapIdentity: () => request<{ user: User; roster: User[]; projects: ProjectDto[]; readMark: number }>('/api/identity'),

  /**
   * Fetch everything after `cursor`.
   * With `wait`, the server holds the request open until something happens, so
   * a change made by a colleague appears here within milliseconds.
   */
  sync: (cursor: number, wait: boolean, signal?: AbortSignal) =>
    request<Delta>(`/api/sync?cursor=${cursor}${wait ? '&wait=1' : ''}`, { signal }),

  readMark: (seq: number) => post<{ ok: true }>('/api/read-mark', { seq }),

  /** Tell the server which file this person is looking at. Best effort. */
  presence: (connection: string, where: { projectId?: string; path?: string }) =>
    post<{ ok: true }>('/api/presence', { connection, ...where }),

  negotiateUpload: (sha256: string, size: number, contentType: string) =>
    post<UploadTicket>('/api/blobs/negotiate', { sha256, size, contentType }),

  confirmUpload: (sha256: string) => post<{ ok: true }>('/api/blobs/confirm', { sha256 }),

  commitFile: (input: { projectId: string; path: string; sha256: string; size: number; message?: string }) =>
    post<{ changes: Change[] }>('/api/files/commit', input).then(published),

  renameFile: (input: { projectId: string; path: string; newName: string; message?: string }) =>
    post<{ changes: Change[] }>('/api/files/rename', input).then(published),

  deleteFiles: (input: { projectId: string; paths: string[]; message?: string }) =>
    post<{ changes: Change[] }>('/api/files/delete', input).then(published),

  downloadUrls: (projectId: string, paths: string[]) =>
    post<{ items: DownloadItem[] }>('/api/files/download', { projectId, paths }),

  /** Claim a path while you work on it. */
  lock: (input: { projectId: string; path: string; note?: string }) =>
    post<{ lock: Lock }>('/api/locks', input)
      .then(r => { published({ locks: [r.lock] }); return r }),

  /** Give a path back. Only the holder may. */
  releaseLock: (id: string) =>
    post<{ lock: Lock }>('/api/locks/release', { id })
      .then(r => { published({ locks: [r.lock] }); return r }),

  /** Ask the holder to release one; writes a comment that @-mentions them. */
  requestUnlock: (id: string, message?: string) =>
    post<{ comment: Comment }>('/api/locks/request', { id, ...(message ? { message } : {}) })
      .then(r => { published({ comments: [r.comment] }); return r }),

  /**
   * Post a comment.
   *
   * `id` is optional but the caller SHOULD supply a stable one and reuse it on
   * retry: the server is idempotent on it, so a retry after a request that the
   * server actually committed returns the existing row rather than writing a
   * duplicate. Without it a dropped acknowledgement means two copies.
   */
  addComment: (input: { id?: string; projectId: string; path?: string; parentId?: string; body: string }) =>
    post<{ comment: Comment }>('/api/comments', input)
      .then(r => { published({ comments: [r.comment] }); return r }),

  editComment: (id: string, body: string) =>
    request<{ comment: Comment }>(`/api/comments/${id}`, { method: 'PATCH', body: JSON.stringify({ body }) })
      .then(r => { published({ comments: [r.comment] }); return r }),

  deleteComment: (id: string) =>
    request<{ comment: Comment }>(`/api/comments/${id}`, { method: 'DELETE' })
      .then(r => { published({ comments: [r.comment] }); return r }),
}
